import { useEffect, useRef, useState, useCallback } from "react";
import api, { API, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";import { Progress } from "@/components/ui/progress";
import { Scale, Lock, KeyRound, Handshake, Download, Eye, FileSignature, FileText, PenLine, Check, Trash2, Send, Copy, Circle } from "lucide-react";
import { copyText } from "@/lib/clipboard";

const ICONS = { scale: Scale, lock: Lock, key: KeyRound, handshake: Handshake };

export default function ClientAgreements({ clientId }) {
  const today = new Date().toISOString().slice(0, 10);
  const [types, setTypes] = useState([]);
  const [dates, setDates] = useState({});
  const [records, setRecords] = useState({});
  const [signKey, setSignKey] = useState(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [signUrl, setSignUrl] = useState("");

  const loadRecords = useCallback(() => {
    api.get(`/clients/${clientId}/agreements`).then((r) => {
      setRecords(Object.fromEntries(r.data.map((x) => [x.atype, x])));
    }).catch(() => {});
  }, [clientId]);

  useEffect(() => {
    api.get("/agreement-types").then((r) => {
      setTypes(r.data);
      setDates(Object.fromEntries(r.data.map((t) => [t.key, today])));
    }).catch(() => {});
    loadRecords();
  }, [clientId, loadRecords]);

  const url = (key, fmt) => `${API}/clients/${clientId}/agreements/${key}/render?fmt=${fmt}&date=${dates[key] || today}&auth=${encodeURIComponent(localStorage.getItem("cp_token") || "")}`;
  const preview = (key) => window.open(url(key, "html"), "_blank");
  const grab = (key, fmt) => {
    const a = document.createElement("a");
    a.href = url(key, fmt);
    document.body.appendChild(a);
    a.click();
    a.remove();
  };
  const unsign = async (key) => { await api.delete(`/clients/${clientId}/agreements/${key}`); toast.success("Signature removed"); loadRecords(); };

  const makeLink = async () => {
    try {
      const { data } = await api.post(`/clients/${clientId}/sign-link`);
      setSignUrl(`${window.location.origin}/sign/${data.token}`);
      setLinkOpen(true);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); }
  };

  const signType = types.find((t) => t.key === signKey);
  const signedCount = types.filter((t) => records[t.key]?.status === "signed").length;
  const pct = types.length ? Math.round((signedCount / types.length) * 100) : 0;

  return (
    <div className="space-y-6">
      <Card className="p-5 bg-card/60 border-border" data-testid="onboarding-status">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg font-semibold">Onboarding — {signedCount} of {types.length} signed</h3>
          <span className="font-mono text-sm">{pct}%</span>
        </div>
        <Progress value={pct} className="h-2 mb-4" />
        <div className="grid sm:grid-cols-2 gap-2">
          {types.map((t) => {
            const done = records[t.key]?.status === "signed";
            return (
              <div key={t.key} className="flex items-center gap-2 text-sm" data-testid={`onboarding-item-${t.key}`}>
                {done ? <Check className="h-4 w-4 text-emerald-400 shrink-0" /> : <Circle className="h-4 w-4 text-muted-foreground shrink-0" />}
                <span className={done ? "text-white" : "text-muted-foreground"}>{t.title}</span>
                <span className={`ml-auto text-xs ${done ? "text-emerald-400" : "text-muted-foreground"}`}>{done ? "Signed" : "Pending"}</span>
              </div>
            );
          })}
        </div>
      </Card>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 text-sm text-muted-foreground flex gap-3 flex-1" data-testid="agreements-intro">
          <FileSignature className="h-5 w-5 shrink-0 mt-0.5 text-primary" />
          <p>Sign online (typed or drawn), or send the client a secure link to sign from their phone. Signed copies are auto-saved to the client's Documents, and a signed POA is referenced in dispute letters.</p>
        </div>
        <Button onClick={makeLink} className="rounded-lg shrink-0" data-testid="send-for-signing-btn"><Send className="h-4 w-4 mr-1.5" /> Send for Signing</Button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {types.map((t) => {
          const Icon = ICONS[t.icon] || FileSignature;
          const rec = records[t.key];
          const signed = rec && rec.status === "signed";
          return (
            <Card key={t.key} className="p-5 bg-card/60 border-border flex flex-col" data-testid={`agreement-${t.key}`}>
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary/15 flex items-center justify-center shrink-0"><Icon className="h-5 w-5 text-primary" /></div>
                <div className="min-w-0">
                  <div className="font-display font-semibold leading-tight">{t.title}</div>
                  <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{t.description}</p>
                </div>
              </div>

              <div className="mt-3">
                {signed ? (
                  <Badge className="bg-emerald-500/20 text-emerald-300" data-testid={`agreement-status-${t.key}`}><Check className="h-3 w-3 mr-1" /> Signed by {rec.signer_name} · {(rec.signed_at || "").slice(0, 10)}</Badge>
                ) : (
                  <Badge variant="secondary" className="text-muted-foreground" data-testid={`agreement-status-${t.key}`}>Not signed</Badge>
                )}
              </div>

              <div className="mt-3 space-y-2">
                <Label className="text-xs">Effective Date</Label>
                <Input type="date" value={dates[t.key] || today} onChange={(e) => setDates({ ...dates, [t.key]: e.target.value })} data-testid={`agreement-date-${t.key}`} />
              </div>

              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-border">
                {signed ? (
                  <Button size="sm" variant="ghost" className="rounded-lg text-destructive" onClick={() => unsign(t.key)} data-testid={`unsign-agreement-${t.key}`}><Trash2 className="h-4 w-4 mr-1" /> Remove sig</Button>
                ) : (
                  <Button size="sm" className="rounded-lg" onClick={() => setSignKey(t.key)} data-testid={`sign-agreement-${t.key}`}><PenLine className="h-4 w-4 mr-1" /> Sign</Button>
                )}
                <Button size="sm" variant="secondary" className="rounded-lg" onClick={() => preview(t.key)} data-testid={`preview-agreement-${t.key}`}><Eye className="h-4 w-4 mr-1" /> Preview</Button>
                <Button size="sm" variant="secondary" className="rounded-lg" onClick={() => grab(t.key, "pdf")} data-testid={`pdf-agreement-${t.key}`}><Download className="h-4 w-4 mr-1" /> PDF</Button>
                <Button size="sm" variant="secondary" className="rounded-lg" onClick={() => grab(t.key, "doc")} data-testid={`word-agreement-${t.key}`}><FileText className="h-4 w-4 mr-1" /> Word</Button>
              </div>
            </Card>
          );
        })}
      </div>

      <SignDialog
        open={!!signKey}
        onClose={() => setSignKey(null)}
        title={signType?.title}
        clientId={clientId}
        atype={signKey}
        effectiveDate={dates[signKey] || today}
        onSigned={() => { setSignKey(null); loadRecords(); }}
      />

      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="bg-card/95 backdrop-blur-xl border-border" data-testid="sign-link-dialog">
          <DialogHeader><DialogTitle className="font-display">Client Signing Link</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Share this secure link with the client. They can review and sign all four agreements from their phone. Signed copies are saved to the client's Documents automatically.</p>
          <div className="flex gap-2">
            <Input readOnly value={signUrl} onFocus={(e) => e.target.select()} data-testid="sign-link-url" />
            <Button onClick={() => copyText(signUrl, "Link copied")} data-testid="copy-sign-link"><Copy className="h-4 w-4" /></Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SignDialog({ open, onClose, title, clientId, atype, effectiveDate, onSigned }) {
  const [mode, setMode] = useState("typed");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const drew = useRef(false);

  useEffect(() => { if (open) { setMode("typed"); setName(""); drew.current = false; } }, [open]);

  const pos = (e, c) => {
    const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };
  const start = (e) => { drawing.current = true; drew.current = true; const c = canvasRef.current; const ctx = c.getContext("2d"); const p = pos(e, c); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const c = canvasRef.current; const ctx = c.getContext("2d"); const p = pos(e, c); ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.strokeStyle = "#0b1a3a"; ctx.lineTo(p.x, p.y); ctx.stroke(); };
  const end = () => { drawing.current = false; };
  const clearCanvas = () => { const c = canvasRef.current; if (c) c.getContext("2d").clearRect(0, 0, c.width, c.height); drew.current = false; };

  const submit = async () => {
    if (!name.trim()) return toast.error("Enter the signer's full name");
    let signature_data = null;
    if (mode === "drawn") {
      if (!drew.current) return toast.error("Please draw your signature first");
      signature_data = canvasRef.current.toDataURL("image/png");
    }
    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/agreements/${atype}/sign`, {
        effective_date: effectiveDate, signer_name: name.trim(),
        signature_type: mode, signature_data,
      });
      toast.success("Agreement signed");
      onSigned();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card/95 backdrop-blur-xl border-border" data-testid="sign-dialog">
        <DialogHeader><DialogTitle className="font-display">Sign: {title}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Signer Full Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. John Doe" data-testid="sign-name" />
          </div>
          <div className="flex gap-2 p-1 rounded-lg bg-secondary w-fit">
            <button onClick={() => setMode("typed")} className={`px-3 py-1.5 rounded-md text-xs font-medium ${mode === "typed" ? "bg-primary text-white" : "text-muted-foreground"}`} data-testid="sign-mode-typed">Type</button>
            <button onClick={() => setMode("drawn")} className={`px-3 py-1.5 rounded-md text-xs font-medium ${mode === "drawn" ? "bg-primary text-white" : "text-muted-foreground"}`} data-testid="sign-mode-drawn">Draw</button>
          </div>
          {mode === "typed" ? (
            <div className="h-24 rounded-lg border border-border bg-white flex items-center justify-center" data-testid="sign-typed-preview">
              <span style={{ fontFamily: "'Brush Script MT','Segoe Script',cursive", fontSize: 34, color: "#0b1a3a" }}>{name || "Your signature"}</span>
            </div>
          ) : (
            <div className="space-y-2">
              <canvas
                ref={canvasRef} width={420} height={110}
                className="w-full rounded-lg border border-border bg-white touch-none"
                onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                onTouchStart={start} onTouchMove={move} onTouchEnd={end}
                data-testid="sign-canvas"
              />
              <Button size="sm" variant="ghost" onClick={clearCanvas} data-testid="sign-clear">Clear</Button>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} className="rounded-lg" data-testid="sign-submit">{saving ? "Signing…" : "Apply Signature"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
